import React, { useContext, useEffect, useState } from 'react';
import { CampaignContext } from '../../context/CampaignContext';
import { useTheme } from '@mui/system';
import {
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    FormControl,
    InputLabel,
    MenuItem,
    OutlinedInput,
    Select
} from '@mui/material';
import { addNewCampaign } from '../sideMenu/BusinessLogic';
import AutoSuggestChips from './AutoSuggestChips';
import { fetchSingleCampaign, updateCampaign } from '../requests/Requests';
import { customersEnum } from '../constants/Enums';
import { validateNewCampaignDto } from './validation/CreateNewCampaignValidation';
import { fetchAllTags } from '../requests/Requests';
import StyledTextFiled from './DialogComponents/StyledTextFiled';
import ButtonCustomStyled from './DialogComponents/DialogActionButton';
import CampaignFolderDropdown from './DialogComponents/CampaignFolderDropdown';
import styles from '../../assets/css/dialogContent.module.css';

const ITEM_HEIGHT = 48;
const ITEM_PADDING_TOP = 8;

const MenuProps = {
    PaperProps: {
        style: {
            maxHeight: ITEM_HEIGHT * 4.5 + ITEM_PADDING_TOP,
            width: 250
        }
    }
};

export default function DialogAddNewCampaign(props) {
    const {
        openDialogAddNewCampaign: openDialogAddNewCampaignBase,
        selectedCampaign: selectedCampaignBase,
        openDialogEditCampaign: openDialogEditCampaignBase,
        campaignsToYearMap: campaignsToYearMapBase
    } = useContext(CampaignContext);

    const [, setOpenDialogAddNewCampaign] = openDialogAddNewCampaignBase;
    const [selectedCampaign, setSelectedCampaign] = selectedCampaignBase;
    const [, setOpenDialogEditCampaign] = openDialogEditCampaignBase;
    const [campaignsToYearMap, setCampaignsToYearMap] = campaignsToYearMapBase;

    const { isEdit } = props;
    const theme = useTheme();

    const [loading, setLoading] = useState(false);
    const [tagsAutosuggest, setTagsAutosuggest] = useState([]);

    const [state, setState] = useState({
        jobNumber: '',
        jobName: '',
        comment: '',
        customer: [],
        week: undefined,
        year: '',
        tags: [],
        folderPath: null,
        virtualFolder : null
    });

    const [validation, setValidation] = useState({
        jobNumberError: '',
        jobNameError: '',
        customerError: '',
        weekError: '',
        yearError: ''
    });

    useEffect(() => {
        const runEffect = async () => {
            if (isEdit) {
                if (!selectedCampaign) {
                    console.error('No campaign selected');
                    return;
                }
                const campaignEdit = { ...selectedCampaign };

                const newState = {
                    jobNumber: campaignEdit.jobNumber,
                    jobName: campaignEdit.jobTitle,
                    comment: campaignEdit.comment,
                    customer: campaignEdit.customerIds,
                    week: campaignEdit.week,
                    year: campaignEdit.year,
                    tags: campaignEdit.tags,
                    folderPath: campaignEdit.folderPath,
                    virtualFolder: campaignEdit.virtualFolder
                };

                setState(newState);
            }

            const tagsAutoSuggest = await fetchAllTags();
            setTagsAutosuggest(tagsAutoSuggest);

            const jobNumberElement = document.getElementById('jobNumber');
            if (jobNumberElement) {
                jobNumberElement.focus();
            }
        };

        runEffect();
    }, []);

    const handleCreateNewCampaign = async () => {
        if (!validateNewCampaignDto(state, validation, setValidation)) {
            return;
        }

        setLoading(true);
        try {
            const newCampaign = await addNewCampaign(state);
            const data = await fetchSingleCampaign(newCampaign.id);
            await handleViewWithNewCampaign(data);

            setSelectedCampaign(data);
            setOpenDialogAddNewCampaign(false);
        } finally {
            setLoading(false);
        }
    };

    const handleEditCampaign = async () => {
        const campaignToEdit = { ...selectedCampaign };


        campaignToEdit.jobTitle = state.jobName;
        campaignToEdit.comment = state.comment;
        campaignToEdit.customerIds = state.customer;
        campaignToEdit.week = state.week;
        campaignToEdit.year = state.year;
        campaignToEdit.tags = state.tags;
        campaignToEdit.folderPath = state.folderPath;
        campaignToEdit.virtualFolder = state.virtualFolder;


        const campaignUpdated = await updateCampaign(campaignToEdit.id, campaignToEdit);

        setSelectedCampaign(campaignUpdated);
        setOpenDialogEditCampaign(false);
    };

    const handleViewWithNewCampaign = async (newCampaign) => {
        const mapCopy = new Map(campaignsToYearMap);

        if (campaignsToYearMap.has(newCampaign.year)) {
            let value = mapCopy.get(newCampaign.year);
            value = [...value, newCampaign];
            mapCopy.set(newCampaign.year, value);
        } else {
            mapCopy.set(newCampaign.year, [newCampaign]);
        }

        setCampaignsToYearMap(mapCopy);
    };

    const handleUpdateState = (value, field) => {
        switch (field) {
            case 'jobNumber':
                setState((currentState) => ({
                    ...currentState,
                    jobNumber: value
                }));
                break;

            case 'jobName':
                setState((currentState) => ({
                    ...currentState,
                    jobName: value
                }));
                break;

            case 'comment':
                setState((currentState) => ({
                    ...currentState,
                    comment: value
                }));
                break;

            case 'week':
                setState((currentState) => ({
                    ...currentState,
                    week: value
                }));
                break;

            case 'year':
                setState((currentState) => ({
                    ...currentState,
                    year: value,
                    virtualFolder: null
                }));
                break;

            default:
                throw new Error('Unknown field');
        }
    };

    const handleClose = () => {
        if (!isEdit) {
            setOpenDialogAddNewCampaign(false);
        } else {
            setOpenDialogEditCampaign(false);
        }
    };

    const handleSave = () => {
        if (isEdit) {
            handleEditCampaign();
        } else {
            handleCreateNewCampaign();
        }
    };

    return (
        <Dialog
            open={true}
            onClose={handleClose}
            fullWidth
            maxWidth="lg"
        >
            <DialogTitle className={styles.title}>
                {isEdit ? 'Rediger kampagnen' : 'Opret ny kampagne'}
            </DialogTitle>

            <DialogContent style={{ padding: '0px 40px 40px 40px' }}>
                <div className={styles.dialogContent}>
                    <div>
                        <StyledTextFiled
                            value={state.jobNumber}
                            disabled={isEdit}
                            label="Jobnummer"
                            state={state}
                            onChange={handleUpdateState}
                            validation={validation.jobNumberError}
                            text="jobNumber"
                        />

                        <div style={{ marginTop: 20 }}>
                            <FormControl
                                fullWidth
                                error={!!validation.customerError}
                            >
                                <InputLabel
                                    id="multiple-name-label"
                                    className={styles.input}
                                >
                                    Kunde
                                </InputLabel>

                                <Select
                                    labelId="demo-multiple-name-label"
                                    id="demo-multiple-name"
                                    multiple
                                    value={state.customer}
                                    onChange={(e) =>
                                        setState((currentState) => ({
                                            ...currentState,
                                            customer: e.target.value
                                        }))
                                    }
                                    input={
                                        <OutlinedInput
                                            label="Name"
                                            className={styles.input}
                                        />
                                    }
                                    MenuProps={MenuProps}
                                >
                                    {customersEnum.map((customer) => (
                                        <MenuItem
                                            key={customer.id}
                                            value={customer.id}
                                            className={styles.input}
                                        >
                                            {customer.displayName}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </div>

                        <StyledTextFiled
                            value={state.jobName}
                            disabled={false}
                            label="Jobnavn"
                            onChange={handleUpdateState}
                            validation={validation.jobNameError}
                            text="jobName"
                        />
                    </div>

                    <div>
                        <StyledTextFiled
                            value={state.week}
                            disabled={false}
                            label="Uge"
                            onChange={handleUpdateState}
                            validation={validation.weekError}
                            text="week"
                        />

                        <div style={{ marginTop: 20 }}>
                            <AutoSuggestChips
                                autoSuggestData={tagsAutosuggest}
                                state={state}
                                setState={setState}
                                style={{
                                    marginTop: 10
                                }}
                            />
                        </div>

                        <StyledTextFiled
                            value={state.year}
                            disabled={false}
                            label="År"
                            onChange={handleUpdateState}
                            validation={validation.yearError}
                            text="year"
                        />
                    </div>

                    <div>
                        <CampaignFolderDropdown
                            year={state.year}
                            virtualFolder={state.virtualFolder}
                            disabled={loading}
                            onChange={(virtualFolder) => {
                                console.log(virtualFolder,"virtual folder");
                                setState((currentState) => ({
                                    ...currentState,
                                    virtualFolder
                                }));
                            }}
                        />
                    </div>
                </div>

                <StyledTextFiled
                    value={state.comment}
                    disabled={false}
                    label="Kommentar"
                    onChange={handleUpdateState}
                    text="comment"
                />
            </DialogContent>

            <DialogActions style={{ padding: '0px 40px 40px 40px' }}>
                <ButtonCustomStyled
                    text="Annuller"
                    onClick={handleClose}
                />

                <ButtonCustomStyled
                    text="Gem kampagne"
                    onClick={handleSave}
                    primary={true}
                />
            </DialogActions>
        </Dialog>
    );
}